import { useState } from 'react';
import type { BookingListItemDTO, CreateBookingRequest, TableDTO, UpdateBookingRequest } from '@six-pockets/shared';
import { TextField, TimeInput } from '@/components/ui';
import { CustomerNameField } from '@/features/customers/components/CustomerNameField';
import { FormRow, ManageFormModal } from '@/features/manage/components/ManageFormModal';
import styles from '@/styles/components/BookingFormModal/style.scss';
import { atTimeAfter, atTimeBefore, atTimeNear, toTimeValue } from '../time';

interface CommonProps {
  busy: boolean;
  error: string | null;
  onClose: () => void;
}

/** Edit an unbilled booking (Bookings page). */
interface EditProps extends CommonProps {
  booking: BookingListItemDTO;
  onSave: (body: UpdateBookingRequest) => void;
}

/** Add a booking after the fact (the ＋ button on a table card). */
interface AddProps extends CommonProps {
  table: TableDTO;
  onAdd: (body: CreateBookingRequest) => void;
}

/**
 * The booking form, for adding and editing. Mount only while open so the fields start fresh. The name works as on a
 * table card: typing lists the existing customers that match, and picking one gives the booking that customer.
 * - Edit: the name, the start time, and the end time once it has ended. Only what changed is sent; the server re-times
 *   the play and re-prices a completed booking.
 * - Add: for when staff were away as play began. A name, the start time, and an end time if they have finished
 *   (left empty, the booking starts in play from that start; only possible when the table has no live booking).
 */
export function BookingFormModal(props: EditProps | AddProps) {
  return 'booking' in props ? <EditBooking {...props} /> : <AddBooking {...props} />;
}

function EditBooking({ booking, busy, error, onSave, onClose }: EditProps) {
  const ended = booking.status === 'completed';
  const [name, setName] = useState(booking.customerName);
  /** The existing customer the name stands for; null once the name is typed over. */
  const [customerId, setCustomerId] = useState(booking.customerId);
  /** The second name (doubles), the same way; emptied, it is removed. */
  const [name2, setName2] = useState(booking.customerName2);
  const [customerId2, setCustomerId2] = useState(booking.customerId2);
  /** One name by default; the second field shows once "+ Second name" is tapped, or when the booking has one. */
  const [showSecond, setShowSecond] = useState(!!booking.customerName2);
  const [start, setStart] = useState(toTimeValue(booking.startedAt));
  const [end, setEnd] = useState(booking.endedAt ? toTimeValue(booking.endedAt) : '');

  const startChanged = start !== toTimeValue(booking.startedAt);
  const endChanged = ended && end !== toTimeValue(booking.endedAt!);
  const startAt = startChanged ? atTimeNear(booking.startedAt, start) : new Date(booking.startedAt);
  const endAt = ended ? (endChanged ? atTimeNear(booking.endedAt!, end) : new Date(booking.endedAt!)) : null;

  const blocked = !start
    ? 'Enter the start time'
    : ended && !end
      ? 'Enter the end time'
      : endAt && endAt <= startAt
        ? 'The end time must be after the start time'
        : undefined;

  const save = () => {
    const body: UpdateBookingRequest = {};
    // A picked customer is sent by id (the booking takes their name); a typed name is sent as text, which also
    // unlinks the customer the booking had.
    if (customerId) {
      if (customerId !== booking.customerId) body.customerId = customerId;
    } else if (name.trim() !== booking.customerName || booking.customerId) {
      body.customerName = name.trim();
    }
    if (customerId2) {
      if (customerId2 !== booking.customerId2) body.customerId2 = customerId2;
    } else if (name2.trim() !== booking.customerName2 || booking.customerId2) {
      body.customerName2 = name2.trim();
    }
    if (startChanged) body.startedAt = startAt.toISOString();
    if (endChanged) body.endedAt = endAt!.toISOString();
    if (Object.keys(body).length === 0) onClose();
    else onSave(body);
  };

  return (
    <ManageFormModal
      title={`Edit ${booking.tableName} booking`}
      submitLabel="Save changes"
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={save}
      onClose={onClose}
    >
      <div className={styles.nameField}>
        <NameField
          value={name}
          customerId={customerId}
          onChange={(n, id) => {
            setName(n);
            setCustomerId(id);
          }}
        />
        {!showSecond && (
          <button type="button" className={styles.nameAction} onClick={() => setShowSecond(true)}>
            + Second name
          </button>
        )}
      </div>
      {showSecond && (
        <div className={styles.nameField}>
          <CustomerNameField
            label="Second name"
            placeholder="Doubles partner"
            value={name2}
            customerId={customerId2}
            autoFocus={!booking.customerName2}
            onChange={(n, id) => {
              setName2(n);
              setCustomerId2(id);
            }}
          />
          <button
            type="button"
            className={`${styles.nameAction} ${styles.removeAction}`}
            onClick={() => {
              setName2('');
              setCustomerId2(null);
              setShowSecond(false);
            }}
          >
            Remove
          </button>
        </div>
      )}
      <FormRow>
        <TimeField label="Start time" value={start} onChange={setStart} />
        {ended ? (
          <TimeField label="End time" value={end} onChange={setEnd} hint="The amount is recalculated when you save." />
        ) : (
          <TextField
            label="End time"
            mono
            value={booking.status === 'paused' ? 'Paused' : 'In play'}
            disabled
            hint="Can be edited once the booking is stopped."
          />
        )}
      </FormRow>
    </ManageFormModal>
  );
}

function AddBooking({ table, busy, error, onAdd, onClose }: AddProps) {
  // A table with a booking in play can only take one that has already ended.
  const tableLive = table.status !== 'available';
  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const now = new Date();
  // The start is always taken as the last time that clock time came round (within the past 24 hours).
  const startAt = start ? atTimeBefore(now, start) : null;
  const endAt = startAt && end ? atTimeAfter(startAt, end) : null;

  const blocked = !startAt
    ? 'Enter the start time'
    : tableLive && !end
        ? 'Enter the end time: this table has a booking in play'
        : endAt && endAt > now
          ? "The end time can't be in the future"
          : undefined;

  const add = () =>
    onAdd({
      customerName: name.trim(),
      ...(customerId && { customerId }),
      startedAt: startAt!.toISOString(),
      ...(endAt && { endedAt: endAt.toISOString() }),
    });

  return (
    <ManageFormModal
      title={`Add booking · ${table.name}`}
      submitLabel={endAt || tableLive ? 'Add booking' : 'Start booking'}
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={add}
      onClose={onClose}
    >
      <NameField
        value={name}
        customerId={customerId}
        onChange={(n, id) => {
          setName(n);
          setCustomerId(id);
        }}
      />
      <FormRow>
        <TimeField label="Start time" value={start} onChange={setStart} />
        <TimeField
          label="End time"
          value={end}
          onChange={setEnd}
          required={tableLive}
          hint={tableLive ? 'Required: this table has a booking in play.' : 'Leave empty if they are still playing.'}
        />
      </FormRow>
    </ManageFormModal>
  );
}

function NameField(props: { value: string; customerId: string | null; onChange: (name: string, customerId: string | null) => void }) {
  return <CustomerNameField label="Name" placeholder="Add name" {...props} />;
}

function TimeField({
  label,
  value,
  onChange,
  hint,
  required = true,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  required?: boolean;
}) {
  // 12-hour (hour · minute · AM/PM) whatever the computer's clock setting; still "HH:MM" in and out.
  return <TimeInput label={label} value={value} onChange={onChange} required={required} hint={hint} />;
}
